import { useState, useCallback } from 'react';
import { 
  CertificateInfo, 
  CertificateFormField, 
  FormState, 
  UseCertificateFormReturn,
  CertificateFormProps 
} from '../../../types/certificate';
import { validateField, validateForm, getTodayString, validateBucketCount } from '../../../utils/validation';
import { certificateService } from '../../../services/certificateService';

/**
 * 合格证表单自定义Hook
 * 管理表单状态、验证和提交逻辑
 */

// 初始表单数据
const getInitialFormData = (initialData?: Partial<CertificateInfo>): CertificateInfo => ({
  orderNumber: '',
  shipmentDate: getTodayString(),
  batchNumber: '',
  bucketCount: 1,
  productionDate: getTodayString(),
  testReportDate: getTodayString(),
  ...initialData
});

interface UseCertificateFormProps {
  onSubmit?: CertificateFormProps['onSubmit'];
  initialData?: Partial<CertificateInfo>;
}

export const useCertificateForm = ({ 
  onSubmit, 
  initialData 
}: UseCertificateFormProps): UseCertificateFormReturn => {
  // 表单数据
  const [data, setData] = useState<CertificateInfo>(() => getInitialFormData(initialData));
  
  // 表单错误
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // 提交状态
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 表单有效性（简单的布尔值，不使用复杂计算）
  const [isValid, setIsValid] = useState(false);

  // 组合表单状态
  const formState: FormState = {
    data,
    errors,
    isSubmitting,
    isValid
  };

  // 简单的验证检查函数（不会导致重渲染）
  const checkFormValidity = useCallback(() => {
    const hasRequiredFields = 
      data.orderNumber.trim() !== '' &&
      data.batchNumber.trim() !== '' &&
      data.bucketCount > 0 &&
      data.productionDate !== '' &&
      data.shipmentDate !== '' &&
      data.testReportDate !== '';

    setIsValid(hasRequiredFields);
  }, [data.orderNumber, data.batchNumber, data.bucketCount, data.productionDate, data.shipmentDate, data.testReportDate]);

  // 验证单个字段
  const validateSingleField = useCallback((field: CertificateFormField): boolean => {
    const value = data[field];
    let error = '';

    if (field === 'bucketCount') {
      error = validateBucketCount(Number(value));
    } else {
      error = validateField(field, value);
    }

    setErrors(prev => ({
      ...prev,
      [field]: error
    }));

    return !error;
  }, [data]);

  // 验证整个表单
  const validateEntireForm = useCallback((): boolean => {
    const validationErrors = validateForm(data);
    const valid = Object.keys(validationErrors).length === 0;

    setErrors(validationErrors);
    setIsValid(valid);
    return valid;
  }, [data]);

  // 处理输入变化
  const handleInputChange = useCallback((field: CertificateFormField, value: string | number) => {
    setData(prev => ({
      ...prev,
      [field]: value
    }));

    // 清除当前字段的错误
    setErrors(prev => ({
      ...prev,
      [field]: ''
    }));

    // 延迟检查表单有效性
    setTimeout(() => {
      checkFormValidity();
    }, 0);
  }, [checkFormValidity]);

  // 处理表单提交
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // 验证表单
    if (!validateEntireForm()) {
      console.log('表单验证失败:', errors);
      return;
    }

    setIsSubmitting(true);

    try {
      if (onSubmit) {
        // 如果提供了自定义提交处理函数
        await onSubmit(data);
      } else {
        // 默认提交到后端服务
        const response = await certificateService.submitCertificate(data);
        console.log('合格证生成成功:', response);
        
        // 可以在这里添加成功提示
        alert(`合格证生成成功！证书ID: ${response.data?.certificateId}`);
      }
    } catch (error) {
      console.error('表单提交失败:', error);
      
      // 显示错误信息
      const errorMessage = error instanceof Error ? error.message : '提交失败，请重试';
      alert(`提交失败: ${errorMessage}`);
      
      setErrors(prev => ({
        ...prev,
        _submit: errorMessage
      }));
    } finally {
      setIsSubmitting(false);
    }
  }, [data, errors, onSubmit, validateEntireForm]);

  // 重置表单
  const resetForm = useCallback(() => {
    const initialData = getInitialFormData();
    setData(initialData);
    setErrors({});
    setIsSubmitting(false);
    setIsValid(false);
  }, []);

  return {
    formState,
    handleInputChange,
    handleSubmit,
    resetForm,
    validateField: validateSingleField,
    validateForm: validateEntireForm
  };
};